import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Card, Form, Input, Button, Typography, Alert, Result, Spin, Flex } from 'antd';
import { LoginOutlined, CalendarOutlined, CoffeeOutlined, TeamOutlined } from '@ant-design/icons';
import NodeLogo from '../Node/NodeLogo';
import { useApi } from '../../hooks/use_api';
import { useNodeInfo } from '../../hooks/use_node_info';
import { useAuth } from '../../context/AuthContext';
import { saveGuest, readGuest } from '../../utils/guest_session';
import PieceBackdrop from '../Skargard/PieceBackdrop';
import Fold from '../Skargard/Fold';
import './Gathering.css';

const { Title, Paragraph, Text } = Typography;

// Same cadence as the member view, so "no gathering" turning into "open"
// shows up here about as soon as it does inside.
const POLL_MS = 15000;

// Where the gathering is, told to a guest before they join — the same three
// states the host picks between (GatheringStatus.jsx).
const STATUS = {
    closed: {
        type: 'info',
        icon: <CalendarOutlined />,
        title: 'No gathering right now',
        text: "You can still join — you'll see it open from inside.",
    },
    waiting: {
        type: 'info',
        icon: <CoffeeOutlined />,
        title: 'The waiting room is open',
        text: "Join now and you'll be let in when the gathering begins.",
    },
    open: {
        type: 'success',
        icon: <TeamOutlined />,
        title: 'The gathering is open',
        text: 'Join to pick your groups and see your rooms.',
    },
};

/**
 * /gathering/join — the public front door for guests, branded as the node
 * (its logo and name, like the navbar) since that's who they're meeting:
 * a name (and, if the
 * host chose "With passcode", the passcode they shared), and you're in the
 * Gathering without an account (backend: GET /api/gathering/guest for the
 * host's guest mode and where the gathering is, POST /api/gathering/guest/join).
 * The identity it returns lives in localStorage (utils/guest_session.js)
 * until it expires; GatheringAccessGate lets it through to /gathering.
 */
function GuestJoinPage() {
    const api = useApi();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const { ready, nodeName, featureGatheringGuestAccess } = useNodeInfo();
    const { isAuthenticated } = useAuth();
    const [joining, setJoining] = useState(false);
    const [error, setError] = useState(null);
    // The host's choice: 'off' | 'passcode' | 'public'; null until known.
    const [mode, setMode] = useState(null);
    // Where the gathering is ('closed' | 'waiting' | 'open', with the host's
    // note when closed) — only told while guests may join.
    const [gathering, setGathering] = useState({ status: null, message: null, singleRoom: false });
    const existing = readGuest();

    useEffect(() => {
        if (!featureGatheringGuestAccess) return undefined;
        let alive = true;
        const load = () => api.get('/api/gathering/guest')
            .then(d => {
                if (!alive) return;
                setMode(d?.mode ?? 'off');
                setGathering({ status: d?.status ?? null, message: d?.message ?? null, singleRoom: !!d?.single_room });
            })
            // A failed first load reads as closed; a failed poll changes nothing.
            .catch(() => { if (alive) setMode(m => m ?? 'off'); });
        load();
        const timer = setInterval(() => { if (!document.hidden) load(); }, POLL_MS);
        return () => { alive = false; clearInterval(timer); };
    }, [api, featureGatheringGuestAccess]);

    if (!ready || (featureGatheringGuestAccess && mode === null)) {
        return <div className="gathering-join"><Spin size="large" /></div>;
    }

    if (!featureGatheringGuestAccess) {
        return (
            <div className="gathering-join">
                <Result
                    status="404"
                    title="Guest access isn't available"
                    subTitle="This node doesn't let guests join its Gathering."
                    extra={<Button type="primary" onClick={() => navigate('/')}>Go to the home page</Button>}
                />
            </div>
        );
    }

    if (mode === 'off') {
        return (
            <div className="gathering-join">
                <Result
                    icon={<NodeLogo size={56} />}
                    title="Guests can't join right now"
                    subTitle="The host hasn't opened the Gathering to guests. Check with them, or try again later."
                    extra={<Button onClick={() => navigate('/gathering')}>I'm a member — sign in</Button>}
                />
            </div>
        );
    }

    const join = ({ name, passcode = '' }) => {
        setJoining(true);
        setError(null);
        api.post('/api/gathering/guest/join', { name, passcode })
            .then(guest => {
                saveGuest(guest);
                navigate('/gathering', { replace: true });
            })
            .catch(err => {
                if (err.status === 403 && mode === 'passcode') setError("That passcode doesn't match. Check with the host.");
                else if (err.status === 403) { setMode('off'); } // the host closed it meanwhile
                else setError(err.message);
            })
            .finally(() => setJoining(false));
    };

    return (
        <PieceBackdrop piece={Fold} followTheme>
            <div className="gathering-join">
                <Card className="gathering-join-card">
                    <Flex vertical gap={16}>
                        <Flex vertical gap={4} align="center">
                            <NodeLogo size={56} />
                            <Title level={3} style={{ margin: '8px 0 0' }}>{nodeName || 'Join the Gathering'}</Title>
                            <Text strong>{nodeName ? 'Join the Gathering as a guest' : 'as a guest'}</Text>
                            <Paragraph type="secondary" style={{ margin: 0, textAlign: 'center' }}>
                                Meet the people here — no account needed. Your guest pass lasts a day.
                            </Paragraph>
                        </Flex>

                        {STATUS[gathering.status] && (
                            <Alert
                                type={STATUS[gathering.status].type}
                                showIcon
                                icon={STATUS[gathering.status].icon}
                                title={STATUS[gathering.status].title}
                                description={gathering.status === 'closed' && gathering.message
                                    ? <span className="gathering-closed-message">{gathering.message}</span>
                                    : gathering.singleRoom
                                        ? 'Everyone’s together in one room — join and come on in.'
                                        : STATUS[gathering.status].text}
                            />
                        )}
                        {params.get('removed') && (
                            <Alert type="warning" showIcon title="The host ended your guest pass" description="Join again if the host lets guests in." />
                        )}
                        {params.get('expired') && (
                            <Alert type="warning" showIcon title="Your guest pass has expired" description="Join again with the passcode to keep going." />
                        )}
                        {existing && (
                            <Alert
                                type="info"
                                showIcon
                                title={`You're already in as ${existing.name}`}
                                action={<Button size="small" type="primary" onClick={() => navigate('/gathering')}>Continue</Button>}
                            />
                        )}
                        {error && <Alert type="error" showIcon title={error} />}

                        <Form layout="vertical" onFinish={join} requiredMark={false} disabled={joining}>
                            <Form.Item
                                name="name"
                                label="Your name"
                                extra="Shown to everyone in the Gathering, marked as a guest."
                                rules={[{ required: true, whitespace: true, message: 'Tell people who you are' }]}
                            >
                                <Input maxLength={80} autoComplete="name" autoFocus />
                            </Form.Item>
                            {mode === 'passcode' && (
                                <Form.Item
                                    name="passcode"
                                    label="Passcode"
                                    rules={[{ required: true, whitespace: true, message: 'Ask the host for the passcode' }]}
                                >
                                    <Input maxLength={64} autoComplete="off" />
                                </Form.Item>
                            )}
                            <Button type="primary" htmlType="submit" block loading={joining}>
                                Join as a guest
                            </Button>
                        </Form>

                        <Text type="secondary" style={{ textAlign: 'center', fontSize: 'var(--fs-sm)' }}>
                            {isAuthenticated
                                ? <>You're signed in — <Link to="/gathering">join as yourself</Link> instead.</>
                                : <>Already a member? <Link to="/gathering"><LoginOutlined /> Sign in</Link></>}
                        </Text>
                    </Flex>
                </Card>
            </div>
        </PieceBackdrop>
    );
}

export default GuestJoinPage;
